<script setup lang="ts">
import { computed } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import { User, MapPin, Bell, Lock, CreditCard } from 'lucide-vue-next'

const page = usePage()

const sections = [
  {
    title: 'Account',
    tabs: [
      { name: 'Profile', href: '/student/settings/profile', icon: User },
      { name: 'Password', href: '/student/settings/password', icon: Lock },
      { name: 'Addresses', href: '/student/settings/addresses', icon: MapPin },
      { name: 'Notifications', href: '/student/settings/notifications', icon: Bell },
      { name: 'Billing', href: '/student/settings/billing', icon: CreditCard },
    ]
  },
]

const allTabs = computed(() => sections.flatMap(section => section.tabs))

const isActive = (tab: { href: string }) => {
  if (!tab || !tab.href || typeof tab.href !== 'string') return false
  const currentPath = page.url.replace(/\/$/, '').split('?')[0]
  const tabPath = tab.href.replace(/\/$/, '')
  return currentPath === tabPath || currentPath.startsWith(tabPath + '/')
}
</script>

<template>
  <DashboardLayout>
    <!-- Header -->
    <div class="border-b border-gray-200 pb-6 mb-6">
      <h1 class="text-3xl font-bold text-gray-900">Account Settings</h1>
      <p class="text-gray-600 mt-2">Manage your profile, addresses, and notification preferences</p>
    </div>

    <!-- Grid Layout: Settings Menu (1 col) + Forms (3 cols) -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
      <!-- Settings Menu - 1 col -->
      <aside class="col-span-1">
        <nav class="space-y-6">
          <div
            v-for="section in sections"
            :key="section.title"
            class="space-y-2"
          >
            <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 mb-3">
              {{ section.title }}
            </h3>
            <div class="space-y-1 flex md:block pb-2 md:pb-0 overflow-x-auto scrollbar-hide">
              <Link
                v-for="tab in section.tabs"
                :key="tab.name"
                :href="tab.href"
                :class="[
                  'group flex items-center px-3 py-2.5 text-sm rounded-lg transition-all relative',
                  isActive(tab)
                    ? 'text-gray-900 font-bold'
                    : 'text-gray-700 font-medium hover:bg-gray-100 hover:text-gray-900'
                ]"
              >
                <component
                  :is="tab.icon"
                  :class="[
                    'mr-3 w-5 h-5',
                    isActive(tab) ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                  ]"
                />
                {{ tab.name }}
                <!-- Blue underline for active tab -->
                <span
                  v-if="isActive(tab)"
                  class="absolute left-0 bottom-0 w-full h-[3px] bg-blue-600 rounded-t-full"
                ></span>
              </Link>
            </div>
          </div>
        </nav>
      </aside>

      <!-- Main Forms Content - 3 cols -->
      <main class="md:col-span-3">
        <slot />
      </main>
    </div>
  </DashboardLayout>
</template>
